/**
 * AGB – Novatus Sales Academy.
 *
 * Anwendungsbereich: digitale Trainingsleistung (B2B + B2C).
 * Sieht zwei Geschäftsfälle vor:
 *   1) Einmalkauf Minuten-Pakete (Prepaid)
 *   2) Abonnements (Pro / Team / Enterprise)
 *
 * Hinweis: vor produktivem Einsatz anwaltliche Prüfung empfohlen,
 * insbesondere wegen § 309 Nr. 9 BGB (Verbraucherverträge),
 * § 312f BGB (Buttonlösung), § 309 Nr. 7 BGB (Haftungsbeschränkung).
 */
import LegalLayout from "../components/LegalLayout";

const AGBPage = () => (
  <LegalLayout
    title="Allgemeine Geschäftsbedingungen"
    subtitle="für die Nutzung der Novatus Sales Academy"
    data-testid="agb-page"
  >
    <section>
      <h2>§ 1 Geltungsbereich</h2>
      <p>
        (1) Diese Allgemeinen Geschäftsbedingungen (im Folgenden „AGB") gelten für alle Verträge,
        die zwischen der NOVATUS Energie GmbH (im Folgenden „Anbieter") und einem Nutzer (im
        Folgenden „Kunde") über die Nutzung der Online-Trainings-Plattform „Novatus Sales Academy"
        (im Folgenden „Plattform") geschlossen werden.
      </p>
      <p>
        (2) Kunde im Sinne dieser AGB kann sowohl ein <strong>Verbraucher</strong> im Sinne des § 13
        BGB als auch ein <strong>Unternehmer</strong> im Sinne des § 14 BGB sein. Soweit in
        einzelnen Bestimmungen zwischen Verbrauchern und Unternehmern unterschieden wird, ist dies
        ausdrücklich kenntlich gemacht.
      </p>
      <p>
        (3) Abweichende Geschäftsbedingungen des Kunden werden nicht anerkannt, es sei denn der
        Anbieter stimmt deren Geltung ausdrücklich schriftlich zu.
      </p>
    </section>

    <section>
      <h2>§ 2 Vertragsgegenstand</h2>
      <p>
        (1) Der Anbieter stellt dem Kunden eine cloudbasierte Trainings-Plattform für KI-gestützte
        Vertriebs-Rollenspiele zur Verfügung. Der Funktionsumfang umfasst u. a. simulierte
        Kundengespräche (Text und Sprache), Sprach-Analytik, KI-Feedback und Trainings-Statistiken.
      </p>
      <p>
        (2) Der Anbieter schuldet keine konkreten Verkaufserfolge. Die Plattform ist ein
        Trainings-Tool und ersetzt weder eine fachliche Vertriebsberatung noch eine rechtliche
        Beratung. Hinweise und Vorschläge der KI dienen ausschließlich Übungs- und Lernzwecken;
        eine Gewähr für deren sachliche Richtigkeit wird nicht übernommen.
      </p>
    </section>

    <section>
      <h2>§ 3 Vertragsschluss</h2>
      <p>
        (1) Die Darstellung der Plattform und der Preismodelle stellt kein bindendes Angebot dar,
        sondern eine Aufforderung zur Abgabe eines Angebots durch den Kunden.
      </p>
      <p>
        (2) Der Kunde gibt durch Auswahl eines Paketes und Klick auf den Button{" "}
        <strong>„Zahlungspflichtig bestellen"</strong> ein verbindliches Angebot ab. Der Anbieter
        nimmt das Angebot durch Bestätigung per E-Mail bzw. durch Freischaltung der gebuchten
        Leistung in der Plattform an.
      </p>
      <p>
        (3) Der Vertragstext wird vom Anbieter gespeichert und dem Kunden mit der
        Bestellbestätigung in Textform übermittelt. Vertragssprache ist Deutsch.
      </p>
    </section>

    <section>
      <h2>§ 4 Leistungsumfang und Pakete</h2>
      <ul>
        <li>
          <strong>Free-Trial:</strong> Jeder Neukunde erhält einmalig 15 Trainings-Minuten kostenlos.
          Die Minuten verfallen 24 Stunden nach Registrierung.
        </li>
        <li>
          <strong>Einmalkauf Minuten-Pakete (Prepaid):</strong> Der Kunde kauft eine feste Anzahl
          Trainings-Minuten zu einem Einmalpreis. Die Minuten sind ab Bereitstellung 12 Monate gültig
          und verfallen anschließend ersatzlos.
        </li>
        <li>
          <strong>Abonnements (Pro / Team / Enterprise):</strong> Der Kunde erhält monatlich eine
          feste Anzahl Trainings-Minuten. Nicht verbrauchte Minuten verfallen am Ende des
          jeweiligen Abrechnungsmonats.
        </li>
      </ul>
      <p>
        Der genaue Leistungsumfang und Preise sind in der Plattform unter „Pakete" einsehbar.
      </p>
    </section>

    <section>
      <h2>§ 5 Team-Lizenzen</h2>
      <p>
        (1) Bei Buchung einer Team- oder Enterprise-Lizenz schließt der Direktor (oder eine andere
        zur Vertretung berechtigte Person) den Vertrag im Namen der gesamten Organisation ab.
      </p>
      <p>
        (2) Der Direktor ist berechtigt, weitere Nutzer („Mitarbeiter") zur Organisation
        hinzuzufügen. Die der Organisation zugeordneten Minuten werden aus einem gemeinsamen
        Minuten-Pool verbraucht.
      </p>
      <p>
        (3) Der Direktor stellt sicher, dass alle eingeladenen Mitarbeiter über die Verarbeitung
        ihrer Daten (insbesondere Audio-Aufzeichnungen) im Rahmen des Trainings informiert sind und
        die für den Betrieb erforderlichen Einwilligungen erteilt haben. Der Anbieter haftet nicht
        für mögliche Verletzungen arbeitsrechtlicher oder mitbestimmungspflichtiger Vorschriften
        innerhalb der Organisation des Kunden.
      </p>
    </section>

    <section>
      <h2>§ 6 Preise und Zahlung</h2>
      <p>
        (1) Alle Preise verstehen sich in Euro <strong>inkl. der gesetzlichen Mehrwertsteuer</strong>{" "}
        (gegenüber Verbrauchern) bzw. <strong>zzgl. der gesetzlichen Mehrwertsteuer</strong>{" "}
        (gegenüber Unternehmern, ausgewiesen separat). Bei Verkäufen ins EU-Ausland an
        Unternehmer mit gültiger USt-IdNr. gilt das Reverse-Charge-Verfahren.
      </p>
      <p>
        (2) Die Zahlung erfolgt online über den Zahlungsdienstleister <strong>Stripe Payments
        Europe, Ltd.</strong> Verfügbare Zahlarten werden vor Abschluss des Bestellvorgangs
        angezeigt.
      </p>
      <p>
        (3) Der Anbieter stellt dem Kunden eine ordnungsgemäße Rechnung mit ausgewiesener USt-IdNr.
        per E-Mail zur Verfügung. Sie kann zusätzlich jederzeit im Profilbereich heruntergeladen werden.
      </p>
      <p>
        (4) Bei Abonnements wird die monatliche Gebühr jeweils zu Beginn des Abrechnungszeitraums
        automatisch eingezogen.
      </p>
    </section>

    <section>
      <h2>§ 7 Vertragslaufzeit, Kündigung</h2>
      <p>
        (1) <strong>Einmalkäufe</strong> begründen kein Dauerschuldverhältnis. Der Vertrag endet mit
        vollständiger Erbringung der Leistung (= Bereitstellung der Minuten); eine Kündigung ist
        nicht erforderlich.
      </p>
      <p>
        (2) <strong>Monats-Abonnements</strong> haben eine Laufzeit von einem Monat und verlängern
        sich automatisch um jeweils einen weiteren Monat, sofern sie nicht spätestens am letzten Tag
        des laufenden Abrechnungsmonats gekündigt werden.
      </p>
      <p>
        (3) Die Kündigung kann jederzeit über die Funktion „Abonnement kündigen" im Profilbereich
        oder per E-Mail an{" "}
        <a href="mailto:info@novatus-se.com">info@novatus-se.com</a> erfolgen (Kündigungsbutton gemäß
        § 312k BGB).
      </p>
      <p>
        (4) <strong>Jahres-Abonnements</strong> (nur Unternehmer-Tarife) haben eine Erstlaufzeit von
        12 Monaten und verlängern sich anschließend automatisch um jeweils einen weiteren Monat,
        sofern sie nicht mit einer Frist von einem Monat zum Ende der Laufzeit gekündigt werden.
      </p>
      <p>
        (5) Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt.
      </p>
      <p>
        (6) <strong>Nicht verbrauchte Minuten verfallen mit Ende des Vertragsverhältnisses
        ersatzlos.</strong> Eine Auszahlung oder anteilige Erstattung ist ausgeschlossen, soweit
        gesetzlich zulässig.
      </p>
    </section>

    <section>
      <h2>§ 8 Widerrufsrecht für Verbraucher</h2>
      <p>
        Verbrauchern steht ein gesetzliches Widerrufsrecht zu. Die ausführliche Widerrufsbelehrung
        sowie das Muster-Widerrufsformular sind unter{" "}
        <a href="/widerruf">/widerruf</a> abrufbar.
      </p>
      <p>
        <strong>Vorzeitiges Erlöschen:</strong> Da es sich bei der Plattform um die Bereitstellung
        digitaler Inhalte / Dienstleistungen handelt, erlischt das Widerrufsrecht gemäß § 356 Abs. 5
        BGB vorzeitig, sobald
      </p>
      <ul>
        <li>der Kunde ausdrücklich zugestimmt hat, dass mit der Ausführung des Vertrages vor Ablauf der Widerrufsfrist begonnen wird, und</li>
        <li>der Kunde bestätigt hat, dass er sein Widerrufsrecht durch diese Zustimmung mit Beginn der Ausführung verliert.</li>
      </ul>
      <p>
        Diese Zustimmung erfolgt im Bestellprozess durch ausdrückliches Anklicken der entsprechenden
        Checkboxen.
      </p>
    </section>

    <section>
      <h2>§ 9 Pflichten des Kunden</h2>
      <p>(1) Der Kunde hat seine Zugangsdaten geheim zu halten und vor unbefugtem Zugriff Dritter zu schützen.</p>
      <p>
        (2) Der Kunde ist verpflichtet, die Plattform ausschließlich im Rahmen ihrer
        bestimmungsgemäßen Verwendung zu nutzen. Insbesondere ist es untersagt:
      </p>
      <ul>
        <li>Zugangsdaten an Dritte weiterzugeben oder den Account zu verkaufen, zu vermieten oder anderweitig zu übertragen,</li>
        <li>die Plattform mit automatisierten Skripten oder Bots zu nutzen oder zu manipulieren,</li>
        <li>Inhalte der Plattform (z. B. KI-Antworten, Aufzeichnungen) zu kommerziellen Drittzwecken zu verwerten,</li>
        <li>rechtswidrige, sittenwidrige oder beleidigende Inhalte im Training zu generieren,</li>
        <li>Sicherheitsmechanismen zu umgehen oder zu deaktivieren.</li>
      </ul>
      <p>
        (3) Bei Verstoß gegen Abs. 2 ist der Anbieter berechtigt, den Account des Kunden vorübergehend
        oder dauerhaft zu sperren. In schwerwiegenden Fällen behält sich der Anbieter eine
        außerordentliche Kündigung sowie Schadensersatzansprüche vor. Bereits geleistete Zahlungen
        werden in diesem Fall nicht erstattet.
      </p>
    </section>

    <section>
      <h2>§ 10 Verfügbarkeit</h2>
      <p>
        (1) Der Anbieter ist um eine möglichst hohe Verfügbarkeit der Plattform bemüht, schuldet
        jedoch keine ununterbrochene Verfügbarkeit. Geplante Wartungsfenster und kurzfristige
        Ausfälle (z. B. durch Störungen Dritter, Internet-Backbone-Ausfälle) sind ausgenommen.
      </p>
      <p>
        (2) Der Anbieter behält sich vor, Funktionen der Plattform fortzuentwickeln, zu verändern
        oder einzustellen, sofern dies für den Kunden zumutbar ist und der vereinbarte
        Vertragszweck nicht wesentlich beeinträchtigt wird.
      </p>
    </section>

    <section>
      <h2>§ 11 Haftungsbeschränkung</h2>
      <p>
        (1) Der Anbieter haftet uneingeschränkt für Schäden, die auf Vorsatz oder grober Fahrlässigkeit
        beruhen, sowie für Schäden aus der Verletzung des Lebens, des Körpers oder der Gesundheit.
      </p>
      <p>
        (2) Bei leichter Fahrlässigkeit haftet der Anbieter nur bei Verletzung wesentlicher
        Vertragspflichten (Kardinalpflichten), deren Erfüllung die ordnungsgemäße Durchführung des
        Vertrages überhaupt erst ermöglicht und auf deren Einhaltung der Kunde regelmäßig vertrauen
        darf. Die Haftung ist in diesem Fall auf den vertragstypischen, vorhersehbaren Schaden
        begrenzt.
      </p>
      <p>
        (3) Im Übrigen ist die Haftung ausgeschlossen, soweit nicht gesetzlich zwingend gehaftet wird
        (z. B. nach dem Produkthaftungsgesetz oder bei garantierten Eigenschaften).
      </p>
      <p>
        (4) Für Schäden, die durch fehlerhafte oder unvollständige KI-Antworten entstehen, übernimmt
        der Anbieter keine Haftung, soweit dies gesetzlich zulässig ist. Der Kunde ist verpflichtet,
        die KI-Vorschläge eigenständig auf Plausibilität und sachliche Richtigkeit zu prüfen.
      </p>
    </section>

    <section>
      <h2>§ 12 Datenschutz</h2>
      <p>
        Hinweise zur Verarbeitung personenbezogener Daten sind unter{" "}
        <a href="/datenschutz">/datenschutz</a> abrufbar.
      </p>
    </section>

    <section>
      <h2>§ 13 Änderungen der AGB</h2>
      <p>
        (1) Der Anbieter ist berechtigt, diese AGB mit einer Vorankündigungsfrist von vier Wochen
        zu ändern. Die Änderungen werden dem Kunden per E-Mail mitgeteilt.
      </p>
      <p>
        (2) Die Änderungen gelten als angenommen, wenn der Kunde nicht innerhalb von vier Wochen ab
        Zugang der Änderungsmitteilung widerspricht. Der Anbieter wird in der Mitteilung auf die
        Bedeutung des Schweigens hinweisen.
      </p>
      <p>
        (3) Widerspricht der Kunde fristgerecht, ist der Anbieter berechtigt, das Vertragsverhältnis
        zum Wirksamwerden der geänderten AGB zu kündigen.
      </p>
    </section>

    <section>
      <h2>§ 14 Schlussbestimmungen</h2>
      <p>
        (1) Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts. Bei
        Verbrauchern gilt diese Rechtswahl nur, soweit hierdurch nicht der durch zwingende
        Bestimmungen des Rechts des Staates des gewöhnlichen Aufenthalts des Verbrauchers gewährte
        Schutz entzogen wird.
      </p>
      <p>
        (2) Erfüllungsort und ausschließlicher Gerichtsstand für alle Streitigkeiten aus diesem
        Vertrag ist der Sitz des Anbieters (Berlin), sofern der Kunde Kaufmann, juristische Person
        des öffentlichen Rechts oder öffentlich-rechtliches Sondervermögen ist.
      </p>
      <p>
        (3) Sollten einzelne Bestimmungen dieser AGB unwirksam sein oder werden, bleibt die
        Wirksamkeit der übrigen Bestimmungen unberührt. An die Stelle der unwirksamen Bestimmung
        tritt die gesetzliche Regelung.
      </p>
    </section>

    <p className="text-xs text-novatus-text-3 mt-8">
      Stand: {new Date().toLocaleDateString("de-DE", { year: "numeric", month: "long" })}
    </p>
  </LegalLayout>
);

export default AGBPage;
